'use client';

import type { QuestionFormEntriesProps } from './QuestionFormEntries.types';
import { Button, ButtonGroup, Flex } from '@/_components';
import { ClientEntryDialog } from './_components/ClientEntryDialog';
import { ClientEntry } from './_components/ClientEntry';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { Question, Questionnaire } from '@/_types/models/questionnaire';
import type { Section } from '@/_types/models/sections';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { addNewEntry, removeEntry } from '@/_api/client-questionnaires';
import { findSpecialSection } from '@/_utils/questionnaire';
import { type CustomClientEntryItem } from '@/_constants/questionnaires';
import { useContext, useMemo, useState, type FC } from 'react';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';

type EntryQuestionsHash = Record<string | number, Record<number, Question>>;
type QuestionsBeingUpdatedHash = Record<string, Record<number, Question>>;

export const QuestionFormEntries: FC<QuestionFormEntriesProps> = ({
  question,
  mode,
  isDisabled,
}) => {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    entryKey: number | null;
  }>({
    isOpen: false,
    entryKey: null,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    reloadQuestions,
    reloadQuestionnaires,
    selectedQuestionnaire,
    selectedSectionQuestions,
  } = useContext(ClientQuestionnaireContext);

  const {
    previewQuestionnaire,
    previewSectionQuestions,
    loadPreviewQuestionnaire,
  } = useContext(PreviewContext);
  const { previewTemplate, onAddClientEntries, onRemoveClientEntry } =
    useContext(UserPreviewContext);

  const { addToast } = useContext(ToastContext);

  const reload = async (): Promise<void> => {
    if (mode === 'admin-preview') return loadPreviewQuestionnaire();
    if (mode === 'admin-template') return;

    reloadQuestionnaires();
    reloadQuestions();
  };

  const questions: Array<Question> = useMemo(() => {
    if (mode === 'admin-preview') return previewSectionQuestions;
    if (mode === 'admin-template') {
      return previewTemplate?.questionnaire_questions || [];
    }

    return selectedSectionQuestions;
  }, [
    mode,
    previewSectionQuestions,
    previewTemplate?.questionnaire_questions,
    selectedSectionQuestions,
  ]);

  const entryQuestionsHash: EntryQuestionsHash = useMemo(() => {
    if (!questions?.length || !question?.id) return {};

    const relevantQuestions = questions.filter(
      (q) =>
        q.trigger_for_follow_up_question !== 'Template' &&
        q.follow_up_question_id === question.id,
    );

    return relevantQuestions.reduce(
      (hash, question) => {
        const entryNumber = question.trigger_for_follow_up_question;

        return {
          ...hash,
          [entryNumber as string]: {
            ...(hash[entryNumber as any] || {}),
            [question.id]: question,
          },
        };
      },
      {} as Record<number, Record<number, Question>>,
    );
  }, [questions, question?.id]);

  const questionnaire: Questionnaire | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewQuestionnaire;
    if (mode === 'admin-template') return previewTemplate as any;

    return selectedQuestionnaire;
  }, [mode, selectedQuestionnaire, previewTemplate, previewQuestionnaire]);

  const entryQuestionsBeingUpdatedHash: QuestionsBeingUpdatedHash =
    useMemo(() => {
      if (!dialogState.entryKey) return {};

      return {
        [dialogState.entryKey]: entryQuestionsHash[dialogState?.entryKey],
      };
    }, [dialogState.entryKey, entryQuestionsHash]);

  const { token } = useContext(UserContext);

  const onToggleDialog = (key: number | null, shouldClose?: boolean) => {
    setDialogState((prev) => ({
      isOpen: shouldClose !== undefined ? !shouldClose : !prev.isOpen,
      entryKey: key,
    }));
  };

  const onAddNewEntry = async (_event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      setIsLoading(true);

      if (mode === 'admin-template') {
        onAddClientEntries(
          question?.id!,
          Object.keys(entryQuestionsHash).length + 1,
          section?.id!,
        );
      } else {
        await addNewEntry({
          companyId: questionnaire?.company_id!,
          questionId: question?.id!,
          token,
        });

        await reload();
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onRemoveEntry = async (entryNumber: string) => {
    try {
      setIsLoading(true);

      if (mode === 'admin-template') {
        return onRemoveClientEntry(question?.id!, entryNumber);
      } else {
        await removeEntry({
          companyId: questionnaire?.company_id!,
          questionnaireQuestionId: question?.id!,
          entryNumber: entryNumber as any,
          token,
        });

        await reload();
        onToggleDialog(null, true);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onCloseDialog = () => onToggleDialog(null, true);

  const section: Section = questionnaire?.sections?.find(
    (s) => s.id === question?.section_id,
  )!;
  const specialSection: CustomClientEntryItem | undefined =
    findSpecialSection(section);

  const companyId: number = questionnaire?.company_id!;
  const questionnaireId: number = questionnaire?.id!;

  return (
    <>
      <ClientEntryDialog
        onClose={onCloseDialog}
        isOpen={dialogState.isOpen}
        isDisabled={isDisabled}
        mode={mode}
        isLoading={isLoading}
        specialSection={specialSection}
        setIsLoading={setIsLoading}
        entryQuestionsBeingUpdatedHash={entryQuestionsBeingUpdatedHash}
        currentEntryKey={dialogState.entryKey}
        questionnaireId={questionnaireId}
        companyId={companyId}
      />
      <Flex isColumn={true} gap={6}>
        {Object.keys(entryQuestionsHash).map(
          (questionKey: any, index: number) => {
            console.debug({
              questionKey,
              entryQuestionsHash,
            });

            const onEdit = () => onToggleDialog(questionKey);
            const onDelete = () => onRemoveEntry(questionKey);

            const currentQuestions: Array<Question> = Object.values(
              entryQuestionsHash[questionKey],
            );
            const firstTextAnswer: string | undefined = currentQuestions?.find(
              (q) => q.question_type === 'text' && !!q.answer,
            )?.answer;

            const clientEntryTitle: string =
              firstTextAnswer ||
              (!!specialSection ? specialSection.itemName : 'Entry') +
                ` ${index + 1}`;
            const clientEntryId: number = index + 1000 + (question?.id ?? 0);

            return (
              <ClientEntry
                key={index}
                title={clientEntryTitle}
                isLoading={isLoading}
                id={clientEntryId}
                isDisabled={isDisabled}
                icon={specialSection?.icon}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            );
          },
        )}
        <ButtonGroup>
          <Button
            intent="primary"
            onClick={onAddNewEntry}
            size="small"
            value={specialSection?.addName || 'Add new entry'}
            iconLeft="plus"
            isDisabled={isLoading || isDisabled}
          />
        </ButtonGroup>
      </Flex>
    </>
  );
};
